import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, mergeProps as _mergeProps, withCtx as _withCtx, unref as _unref, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "form-group" }
const _hoisted_2 = ["for"]
const _hoisted_3 = {
  key: 0,
  class: "text-red-500 ml-1"
}
const _hoisted_4 = { class: "relative" }
const _hoisted_5 = {
  key: 0,
  class: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
}
const _hoisted_6 = {
  key: 0,
  value: "",
  disabled: ""
}
const _hoisted_7 = ["value"]
const _hoisted_8 = {
  key: 1,
  class: "absolute inset-y-0 right-0 pr-3 flex items-center"
}
const _hoisted_9 = {
  key: 1,
  class: "mt-2 text-sm text-gray-500 dark:text-gray-400"
}
const _hoisted_10 = {
  key: 2,
  class: "mt-2 text-sm text-red-600 dark:text-red-400 font-medium"
}
const _hoisted_11 = {
  key: 3,
  class: "mt-1 text-xs text-gray-500 dark:text-gray-400 text-right"
}

import { computed, ref } from 'vue'
import { Eye, EyeOff } from 'lucide-vue-next'

export interface Option {
  value: string | number
  label: string
}

export interface Props {
  modelValue?: string | number
  label?: string
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'textarea' | 'select'
  placeholder?: string
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  error?: string
  helperText?: string
  prefixIcon?: any
  suffixIcon?: any
  min?: number
  max?: number
  step?: number
  maxlength?: number
  rows?: number
  options?: Option[]
  showCharCount?: boolean
  id?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BaseInput',
  props: {
    modelValue: { default: '' },
    label: { default: '' },
    type: { default: 'text' },
    placeholder: { default: '' },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    error: { default: '' },
    helperText: { default: '' },
    prefixIcon: { default: undefined },
    suffixIcon: { default: undefined },
    min: { default: undefined },
    max: { default: undefined },
    step: { default: undefined },
    maxlength: { default: undefined },
    rows: { default: 3 },
    options: { default: () => [] },
    showCharCount: { type: Boolean, default: false },
    id: { default: undefined }
  },
  emits: ["update:modelValue", "blur", "focus", "change", "input"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// Password visibility toggle
const passwordVisible = ref(false)
const showPasswordToggle = computed(() => props.type === 'password')

const togglePasswordVisibility = () => {
  passwordVisible.value = !passwordVisible.value
}

// Computed properties
const inputId = computed(() => props.id || `input-${Math.random().toString(36).substr(2, 9)}`)

const actualInputType = computed(() => {
  if (props.type === 'password') {
    return passwordVisible.value ? 'text' : 'password'
  }
  return props.type
})

const inputTag = computed(() => {
  if (props.type === 'textarea') return 'textarea'
  if (props.type === 'select') return 'select'
  return 'input'
})

const inputValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const inputClasses = computed(() => {
  const baseClasses = [
    'block',
    'w-full',
    'text-base',
    'border',
    'rounded-xl',
    'shadow-sm',
    'transition-all',
    'duration-200',
    'ease-in-out',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-blue-500/20',
    'focus:border-blue-500',
    'bg-white',
    'dark:bg-gray-700',
    'text-gray-900',
    'dark:text-white',
    'placeholder-gray-500',
    'dark:placeholder-gray-400'
  ]

  // Padding classes based on icons
  const paddingClasses = []
  if (props.prefixIcon) {
    paddingClasses.push('pl-10')
  } else {
    paddingClasses.push('px-4')
  }

  if (props.suffixIcon || showPasswordToggle.value) {
    paddingClasses.push('pr-10')
  } else if (!props.prefixIcon) {
    paddingClasses.push('px-4')
  }

  // Vertical padding
  if (props.type === 'textarea') {
    paddingClasses.push('py-3')
  } else {
    paddingClasses.push('py-3')
  }

  // State classes
  const stateClasses = []
  if (props.error) {
    stateClasses.push(
      'border-red-500',
      'focus:border-red-500',
      'focus:ring-red-500/20'
    )
  } else {
    stateClasses.push(
      'border-gray-300',
      'dark:border-gray-600',
      'hover:border-gray-400',
      'dark:hover:border-gray-500'
    )
  }

  if (props.disabled) {
    stateClasses.push(
      'bg-gray-50',
      'dark:bg-gray-800',
      'text-gray-500',
      'dark:text-gray-400',
      'cursor-not-allowed'
    )
  }

  return [...baseClasses, ...paddingClasses, ...stateClasses]
})

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  let value: string | number = target.value
  
  if (props.type === 'number') {
    value = target.value === '' ? '' : Number(target.value)
  }
  
  emit('update:modelValue', value)
  emit('input', value)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_ctx.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          for: inputId.value,
          class: "block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
        }, [
          _createTextVNode(_toDisplayString(_ctx.label) + " ", 1),
          (_ctx.required)
            ? (_openBlock(), _createElementBlock("span", _hoisted_3, "*"))
            : _createCommentVNode("", true)
        ], 8, _hoisted_2))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_4, [
      (_ctx.prefixIcon)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.prefixIcon), { class: "h-5 w-5 text-gray-400 dark:text-gray-500" }))
          ]))
        : _createCommentVNode("", true),
      (_openBlock(), _createBlock(_resolveDynamicComponent(inputTag.value), _mergeProps({
        id: inputId.value,
        modelValue: inputValue.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((inputValue).value = $event))
      }, inputTag.value === 'input' ? { type: actualInputType.value } : {}, {
        placeholder: _ctx.placeholder,
        disabled: _ctx.disabled,
        readonly: _ctx.readonly,
        required: _ctx.required,
        min: _ctx.min,
        max: _ctx.max,
        step: _ctx.step,
        maxlength: _ctx.maxlength,
        rows: _ctx.rows,
        class: inputClasses.value,
        onBlur: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('blur'))),
        onFocus: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('focus'))),
        onInput: handleInput,
        onChange: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('change', $event)))
      }), {
        default: _withCtx(() => [
          (_ctx.type === 'select')
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                (_ctx.placeholder)
                  ? (_openBlock(), _createElementBlock("option", _hoisted_6, _toDisplayString(_ctx.placeholder), 1))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.options, (option) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: option.value,
                    value: option.value
                  }, _toDisplayString(option.label), 9, _hoisted_7))
                }), 128))
              ], 64))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 16, ["id", "modelValue", "placeholder", "disabled", "readonly", "required", "min", "max", "step", "maxlength", "rows", "class"])),
      (_ctx.suffixIcon || showPasswordToggle.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            (showPasswordToggle.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  class: "text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 focus:outline-none",
                  onClick: togglePasswordVisibility
                }, [
                  (_openBlock(), _createBlock(_resolveDynamicComponent(passwordVisible.value ? _unref(EyeOff) : _unref(Eye)), { class: "h-5 w-5" }))
                ]))
              : (_ctx.suffixIcon)
                ? (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.suffixIcon), {
                    key: 1,
                    class: "h-5 w-5 text-gray-400 dark:text-gray-500"
                  }))
                : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ]),
    (_ctx.helperText && !_ctx.error)
      ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(_ctx.helperText), 1))
      : _createCommentVNode("", true),
    (_ctx.error)
      ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(_ctx.error), 1))
      : _createCommentVNode("", true),
    (_ctx.showCharCount && _ctx.maxlength)
      ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(String(inputValue.value)?.length || 0) + "/" + _toDisplayString(_ctx.maxlength), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})